import { SETS, RARITIES, SET_CODE_TO_SLUG, SET_SLUG_TO_CODE, setReleaseDate } from '../../constants';
import type { CollectionEntry, RiftboundCard } from '../../types';
import { cardIsFoilOnly } from '../../utils/collectionFoil';
import {
  cardPrintVariant,
  type CardPrintVariant,
} from '../../utils/cardPrintVariant';

export const RARITY_ROW = [...RARITIES];

/** Shareable path for a set’s collection stats sheet, e.g. `/collection/sets/spiritforged`. */
export function collectionSetStatsPath(setCode: string): string {
  const code = normSet(setCode);
  const slug = SET_CODE_TO_SLUG[code] ?? code.toLowerCase();
  return `/collection/sets/${slug}`;
}

/** Public collection deep link for a set stats sheet, e.g. `/collections/gil/sets/spiritforged`. */
export function publicCollectionSetStatsPath(playerSlug: string, setCode: string): string {
  const clean = playerSlug.trim().toLowerCase();
  const code = normSet(setCode);
  const setSlug = SET_CODE_TO_SLUG[code] ?? code.toLowerCase();
  return `/collections/${encodeURIComponent(clean)}/sets/${setSlug}`;
}

/** Resolve `/collection/sets/:setSlug` → uppercase set code, or null if unknown. */
export function setCodeFromCollectionStatsSlug(slug: string | undefined): string | null {
  if (!slug) return null;
  const key = slug.trim().toLowerCase();
  if (!key) return null;
  const mapped = SET_SLUG_TO_CODE[key];
  if (mapped) return mapped.toUpperCase();
  const code = key.toUpperCase();
  if (SET_CODE_TO_SLUG[code] || SETS.some((s) => s.code.toUpperCase() === code)) {
    return code;
  }
  return null;
}

/** `color` prop values for rarity diamonds (Chakra semantic + default palette). */
export const COLLECTION_RARITY_COLOR: Record<string, string> = {
  Common: 'textMuted',
  Uncommon: 'green.400',
  Rare: 'blue.400',
  Epic: 'purple.400',
  Showcase: 'accent',
  Unknown: 'textMuted',
};

export function normSet(s: string | undefined): string {
  return (s || '').toUpperCase();
}

/** Human-readable set title: prefers Riftcodex names from `/api/facets` `setNames`, then local {@link SETS} fallbacks. */
export function setDisplayName(code: string, labels?: Record<string, string>): string {
  const u = normSet(code);
  const fromApi = labels?.[u];
  if (fromApi) return fromApi;
  const meta = SETS.find((x: (typeof SETS)[number]) => x.code.toUpperCase() === u);
  return meta?.name ?? code;
}

export function sortSetTabs(codes: string[]): string[] {
  const order = new Map(
    SETS.map((s: (typeof SETS)[number], i: number) => [s.code.toUpperCase(), i] as [string, number])
  );
  const unique = [...new Set(codes.map((c) => normSet(c)))];
  return unique.sort((a, b) => {
    const da = setReleaseDate(a);
    const db = setReleaseDate(b);
    if (da && db && da !== db) return da.localeCompare(db);
    if (da && !db) return -1;
    if (!da && db) return 1;

    const ia: number = order.has(a) ? (order.get(a) as number) : Number.MAX_SAFE_INTEGER;
    const ib: number = order.has(b) ? (order.get(b) as number) : Number.MAX_SAFE_INTEGER;
    if (ia !== ib) return ia - ib;
    return a.localeCompare(b);
  });
}

export function collectOwnedQuantityByRarity(
  entries: Record<string, CollectionEntry>,
  cardById: Map<string, RiftboundCard>
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of Object.values(entries)) {
    if (e.quantity <= 0) continue;
    const card = cardById.get(e.cardId);
    const r = card?.rarity?.trim() ? card.rarity : 'Unknown';
    out[r] = (out[r] || 0) + e.quantity;
  }
  return out;
}

export function collectOwnedUniqueBySetRarity(
  entries: Record<string, CollectionEntry>,
  cardById: Map<string, RiftboundCard>,
  countsAsOwned?: (entry: CollectionEntry, card: RiftboundCard) => boolean,
): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  for (const e of Object.values(entries)) {
    const card = cardById.get(e.cardId);
    if (!card?.setCode) continue;
    const owned = countsAsOwned ? countsAsOwned(e, card) : e.quantity > 0;
    if (!owned) continue;
    const set = normSet(card.setCode);
    const r = card.rarity?.trim() ? card.rarity : 'Unknown';
    if (!out[set]) out[set] = {};
    out[set][r] = (out[set][r] || 0) + 1;
  }
  return out;
}

export function rarityArtForSet(
  setCode: string,
  rarity: string,
  cards: RiftboundCard[]
): string | undefined {
  const u = normSet(setCode);
  const c = cards.find(
    (x) =>
      normSet(x.setCode) === u &&
      (x.rarity?.trim() ? x.rarity : 'Unknown') === rarity &&
      (x.thumbnailUrl || x.imageUrl)
  );
  return c?.thumbnailUrl || c?.imageUrl;
}

export function raritiesForSetGrid(
  cat: Record<string, number>,
  own: Record<string, number>
): string[] {
  const row = [...RARITY_ROW] as readonly string[];
  const base = [
    ...row.filter((r) => (cat[r] || 0) > 0 || (own[r] || 0) > 0),
    ...(cat.Unknown || own.Unknown ? (['Unknown'] as const) : []),
  ];
  return base.filter((r, i, arr) => arr.indexOf(r) === i);
}

/** Sum of per-rarity counts (unique definitions per bucket). */
export function sumRarityMap(values: Record<string, number>): number {
  let s = 0;
  for (const v of Object.values(values)) s += Number(v) || 0;
  return s;
}

/**
 * Overall set completion: unique cards owned (any rarity) / unique catalog cards in set.
 */
export function setOverallCompletion(
  catalogByRarity: Record<string, number>,
  ownedByRarity: Record<string, number>
): { ownedUnique: number; catalogUnique: number; pct: number | null } {
  const catalogUnique = sumRarityMap(catalogByRarity);
  const ownedUnique = sumRarityMap(ownedByRarity);
  if (catalogUnique <= 0) {
    return { ownedUnique, catalogUnique, pct: null };
  }
  return {
    ownedUnique,
    catalogUnique,
    pct: Math.min(100, (ownedUnique / catalogUnique) * 100),
  };
}

/**
 * Unique catalog cards owned across every set in `catalogBySet`, over total unique catalog
 * definitions in those sets (same basis as each set tab’s percentage).
 */
export function allSetsCatalogCompletion(
  catalogBySet: Record<string, Record<string, number>>,
  ownedBySetRarity: Record<string, Record<string, number>>,
  options?: { excludeSetCodes?: Iterable<string> },
): { ownedUnique: number; catalogUnique: number; pct: number | null } {
  const exclude = new Set(
    [...(options?.excludeSetCodes ?? [])].map((c) => normSet(c)),
  );
  let catalogSum = 0;
  let ownedSum = 0;
  for (const [rawCode, catRarities] of Object.entries(catalogBySet)) {
    if (!catRarities || typeof catRarities !== 'object') continue;
    const setCode = normSet(rawCode);
    if (exclude.has(setCode)) continue;
    const ownedRarities = ownedBySetRarity[setCode] || {};
    const row = setOverallCompletion(catRarities, ownedRarities);
    catalogSum += row.catalogUnique;
    ownedSum += row.ownedUnique;
  }
  if (catalogSum <= 0) {
    return { ownedUnique: ownedSum, catalogUnique: catalogSum, pct: null };
  }
  return {
    ownedUnique: ownedSum,
    catalogUnique: catalogSum,
    pct: Math.min(100, (ownedSum / catalogSum) * 100),
  };
}

export type VariantCompletionCounts = {
  owned: number;
  catalog: number;
  pct: number | null;
};

export type SetVariantCompletion = Record<CardPrintVariant | 'all', VariantCompletionCounts>;

function emptyVariantCounts(): VariantCompletionCounts {
  return { owned: 0, catalog: 0, pct: null };
}

function withPct(owned: number, catalog: number): VariantCompletionCounts {
  if (catalog <= 0) return { owned, catalog, pct: null };
  return { owned, catalog, pct: Math.min(100, (owned / catalog) * 100) };
}

/**
 * Unique ownership vs catalog size for standard / alt-art / overnumbered prints in a set.
 * Requires the full set catalog list (not just owned cards).
 */
export function collectSetVariantCompletion(
  setCards: RiftboundCard[],
  entries: Record<string, CollectionEntry>,
): SetVariantCompletion {
  const catalog: Record<CardPrintVariant | 'all', number> = {
    all: 0,
    standard: 0,
    altArt: 0,
    overnumbered: 0,
  };
  const owned: Record<CardPrintVariant | 'all', number> = {
    all: 0,
    standard: 0,
    altArt: 0,
    overnumbered: 0,
  };

  for (const card of setCards) {
    const variant = cardPrintVariant(card);
    catalog.all += 1;
    catalog[variant] += 1;
    if ((entries[card.id]?.quantity ?? 0) > 0) {
      owned.all += 1;
      owned[variant] += 1;
    }
  }

  return {
    all: withPct(owned.all, catalog.all),
    standard: withPct(owned.standard, catalog.standard),
    altArt: withPct(owned.altArt, catalog.altArt),
    overnumbered: withPct(owned.overnumbered, catalog.overnumbered),
  };
}

export function emptySetVariantCompletion(): SetVariantCompletion {
  return {
    all: emptyVariantCounts(),
    standard: emptyVariantCounts(),
    altArt: emptyVariantCounts(),
    overnumbered: emptyVariantCounts(),
  };
}

/** True when the entry includes at least one foil copy (foil-only prints count whenever owned). */
export function entryHasFoil(
  card: Pick<RiftboundCard, 'rarity'>,
  entry: Pick<CollectionEntry, 'quantity' | 'quantityFoil'> | undefined,
): boolean {
  if (!entry || entry.quantity <= 0) return false;
  if (cardIsFoilOnly(card)) return true;
  return (entry.quantityFoil ?? 0) > 0;
}

/**
 * Unique cards owned as foil vs catalog size for a set.
 * Requires the full set catalog list for an accurate denominator.
 */
export function collectSetFoilCompletion(
  setCards: RiftboundCard[],
  entries: Record<string, CollectionEntry>,
): VariantCompletionCounts {
  let owned = 0;
  const catalog = setCards.length;
  for (const card of setCards) {
    if (entryHasFoil(card, entries[card.id])) owned += 1;
  }
  return withPct(owned, catalog);
}

/**
 * Per-set unique foil ownership from already-loaded owned cards + catalog rarity totals.
 * Useful for set picker faces without fetching each set’s full catalog.
 */
export function collectOwnedFoilUniqueBySet(
  entries: Record<string, CollectionEntry>,
  cardById: Map<string, RiftboundCard>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of Object.values(entries)) {
    const card = cardById.get(e.cardId);
    if (!card?.setCode || !entryHasFoil(card, e)) continue;
    const set = normSet(card.setCode);
    out[set] = (out[set] || 0) + 1;
  }
  return out;
}
